import { FC, useMemo } from 'react';

import { Box } from '@bonusly/design-system';

import GhostSVG from './images/ghost.svg';

// import Ghost2SVG from './images/ghost-2.svg';
// Add more ghost images here as you create them:
// import Ghost3SVG from './images/ghost-3.svg';
// import Ghost4SVG from './images/ghost-4.svg';

// Array of available ghost images - add new ones here to have them randomly appear!
const GHOST_IMAGES = [
	GhostSVG,
	// Ghost2SVG, // Uncomment when ready to use
	// Ghost3SVG,
	// Ghost4SVG,
];

/**
 * Flying Ghosts Component
 * Renders 2-3 spooky ghosts that fly across the screen at random positions
 * Each ghost appears randomly every ~60-90 seconds
 *
 * To add new ghost images:
 * 1. Import the SVG at the top
 * 2. Add it to the GHOST_IMAGES array
 * 3. Done! It will be randomly selected
 */
export const FlyingGhosts: FC = () => {
	// Generate random configurations on mount for unpredictable timing
	const ghosts = useMemo(() => {
		const randomDelay = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1) + min);
		const randomGhostImage = () => GHOST_IMAGES[Math.floor(Math.random() * GHOST_IMAGES.length)];

		return [
			{
				delay: `${randomDelay(0, 30)}s`,
				duration: `${randomDelay(12, 18)}s`,
				verticalPosition: `${randomDelay(10, 70)}%`, // Random position from 10% to 70% of screen height
				size: `${randomDelay(60, 90)}px`,
				image: randomGhostImage(),
				// Random animation parameters for variety
				bobHeight: randomDelay(20, 60),
				wobbleAmount: randomDelay(5, 15),
				waviness: randomDelay(30, 80),
			},
			{
				delay: `${randomDelay(60, 90)}s`,
				duration: `${randomDelay(12, 18)}s`,
				verticalPosition: `${randomDelay(10, 70)}%`,
				size: `${randomDelay(60, 90)}px`,
				image: randomGhostImage(),
				bobHeight: randomDelay(20, 60),
				wobbleAmount: randomDelay(5, 15),
				waviness: randomDelay(30, 80),
			},
			{
				delay: `${randomDelay(120, 150)}s`,
				duration: `${randomDelay(12, 18)}s`,
				verticalPosition: `${randomDelay(10, 70)}%`,
				size: `${randomDelay(60, 90)}px`,
				image: randomGhostImage(),
				bobHeight: randomDelay(20, 60),
				wobbleAmount: randomDelay(5, 15),
				waviness: randomDelay(30, 80),
			},
		];
	}, []);

	return (
		<>
			{ghosts.map((ghost, index) => (
				<Box
					key={index}
					position="fixed"
					width={ghost.size}
					height={ghost.size}
					top={ghost.verticalPosition}
					left="-200px"
					pointerEvents="none"
					zIndex={2}
					sx={{
						// Unique animation name per ghost for different movements
						animation: `flyAcross${index} ${ghost.duration} linear ${ghost.delay} infinite`,
						// Make the ghost white/semi-transparent
						'& img': {
							filter: 'invert(1) opacity(0.15)',
							width: '100%',
							height: '100%',
						},
						// Unique keyframes for each ghost with random bobbing, wobbling, and wavy path
						[`@keyframes flyAcross${index}`]: {
							'0%': {
								left: '-200px',
								transform: `translateY(0px) rotate(${-ghost.wobbleAmount}deg) scale(1)`,
							},
							'15%': {
								transform: `translateY(${-ghost.bobHeight * 0.5}px) rotate(${ghost.wobbleAmount}deg) scale(1.05)`,
							},
							'30%': {
								transform: `translateY(${-ghost.waviness}px) rotate(${-ghost.wobbleAmount * 0.5}deg) scale(0.95)`,
							},
							'50%': {
								transform: `translateY(${-ghost.bobHeight}px) rotate(${ghost.wobbleAmount}deg) scale(1.1)`,
							},
							'65%': {
								transform: `translateY(${-ghost.waviness * 0.7}px) rotate(${-ghost.wobbleAmount}deg) scale(1)`,
							},
							'80%': {
								transform: `translateY(${-ghost.bobHeight * 0.3}px) rotate(${ghost.wobbleAmount * 0.7}deg) scale(1.05)`,
							},
							'100%': {
								left: 'calc(100vw + 200px)',
								transform: `translateY(0px) rotate(${-ghost.wobbleAmount}deg) scale(1)`,
							},
						},
					}}
				>
					<img src={ghost.image} alt="" />
				</Box>
			))}
		</>
	);
};
